import axios from "axios";

import { useCallback, useState } from "react";

import { getEnvironment } from "@/shared/config/env";

import { useCreateGuestReleaseWithProgress } from "../hooks/useGuest";

interface CreateGuestReleaseModalProps {
  onClose: () => void;
}

type FileSlot = "installer" | "updater";

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;

  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const CreateGuestReleaseModal = ({ onClose }: CreateGuestReleaseModalProps) => {
  const [version, setVersion] = useState("");

  const [releaseNotes, setReleaseNotes] = useState("");

  // Windows (wizard .exe) o macOS (DMG notarizado). El updater standalone es

  // solo-windows (el backend lo rechaza para otras plataformas); mac se

  // distribuye download-only por ahora.

  const [platform, setPlatform] = useState<"windows" | "macos">("windows");

  const environment = getEnvironment();

  const [installer, setInstaller] = useState<File | null>(null);

  const [updater, setUpdater] = useState<File | null>(null);

  const [error, setError] = useState("");

  const [draggingSlot, setDraggingSlot] = useState<FileSlot | null>(null);

  const createRelease = useCreateGuestReleaseWithProgress();

  const getInstallerAccept = () =>
    platform === "macos" ? ".dmg,.zip" : ".exe,.msix,.zip";

  const getInstallerDescription = () =>
    platform === "macos"
      ? "Archivos soportados: .dmg, .zip"
      : "Archivos soportados: .exe, .msix, .zip";

  const handleDragEnter = useCallback(
    (slot: FileSlot) => (e: React.DragEvent) => {
      e.preventDefault();

      e.stopPropagation();

      setDraggingSlot(slot);
    },

    [],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    e.stopPropagation();

    setDraggingSlot(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();

    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (slot: FileSlot) => (e: React.DragEvent) => {
      e.preventDefault();

      e.stopPropagation();

      setDraggingSlot(null);

      const droppedFile = e.dataTransfer.files[0];

      if (droppedFile) {
        if (slot === "installer") {
          setInstaller(droppedFile);
        } else {
          setUpdater(droppedFile);
        }

        setError("");
      }
    },

    [],
  );

  const handleFileSelect =
    (slot: FileSlot) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];

      if (selectedFile) {
        if (slot === "installer") {
          setInstaller(selectedFile);
        } else {
          setUpdater(selectedFile);
        }

        setError("");
      }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError("");

    if (!version.trim()) {
      setError("La versión es requerida");

      return;
    }

    if (!installer) {
      setError("Debes seleccionar el archivo del instalador");

      return;
    }

    if (!releaseNotes.trim()) {
      setError("Las notas de la versión son requeridas");

      return;
    }

    try {
      await createRelease.mutateAsync({
        version,

        releaseNotes,

        platform,

        environment,

        installer,

        ...(platform === "windows" && updater ? { updater } : {}),
      });

      onClose();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const serverMessage = err.response?.data?.message;

        if (serverMessage) {
          setError(serverMessage);
        } else if (err.code === "ECONNABORTED") {
          setError("La subida tardó demasiado tiempo. Intenta nuevamente.");
        } else if (err.code === "ERR_NETWORK") {
          setError("Error de conexión. Verifica tu conexión a internet.");
        } else if (err.response?.status === 413) {
          setError("El archivo es demasiado grande.");
        } else if (err.response?.status && err.response.status >= 500) {
          setError("Error del servidor. Intenta más tarde.");
        } else {
          setError(err.message || "Error al crear la versión");
        }
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error al crear la versión");
      }
    }
  };

  const isSubmitDisabled = createRelease.isPending || !installer;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-2xl rounded-lg border border-slate-700 bg-surface p-6 shadow-xl">
        <h2 className="mb-4 text-xl font-semibold text-slate-100">
          Nueva Versión de Guest
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Versión *
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              className="w-full rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Plataforma *
            </label>
            <div role="group" aria-label="Plataforma" className="flex gap-2">
              {(["windows", "macos"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={platform === p}
                  disabled={createRelease.isPending}
                  onClick={() => {
                    setPlatform(p);

                    // El instalador y el updater ya seleccionados pueden no

                    // corresponder a la nueva plataforma: se limpian.

                    setInstaller(null);

                    setUpdater(null);

                    setError("");
                  }}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition disabled:opacity-50 ${
                    platform === p
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-slate-700 bg-background text-slate-300 hover:border-slate-600"
                  }`}
                >
                  {p === "windows" ? "Windows" : "macOS"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-medium text-slate-300">
              Instalador *
            </label>
            <div
              onDragEnter={handleDragEnter("installer")}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop("installer")}
              className={`relative rounded-lg border-2 border-dashed p-8 text-center transition ${
                draggingSlot === "installer"
                  ? "border-primary bg-primary/10"
                  : "border-slate-700 bg-surface hover:border-slate-600"
              }`}
            >
              <input
                type="file"
                accept={getInstallerAccept()}
                onChange={handleFileSelect("installer")}
                className="absolute inset-0 cursor-pointer opacity-0"
                disabled={createRelease.isPending}
              />
              <div className="flex flex-col items-center gap-2">
                <svg
                  className="h-12 w-12 text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="text-sm text-slate-300">
                  Arrastra el instalador aquí o{" "}
                  <span className="text-primary">
                    haz clic para seleccionar
                  </span>
                </p>
                <p className="text-xs text-slate-500">
                  {getInstallerDescription()}
                </p>
              </div>
            </div>

            {installer && (
              <div className="mt-4">
                <div className="flex items-center justify-between rounded-md border border-slate-700 bg-surface p-3">
                  <div className="flex items-center gap-3">
                    <svg
                      className="h-5 w-5 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-slate-200">
                        {installer.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatFileSize(installer.size)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInstaller(null)}
                    className="text-red-400 transition hover:text-red-300"
                    disabled={createRelease.isPending}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {platform === "windows" && (
            <div className="flex flex-col">
              <label className="mb-2 block text-sm font-medium text-slate-300">
                Updater (opcional)
              </label>
              <div
                onDragEnter={handleDragEnter("updater")}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop("updater")}
                className={`relative rounded-lg border-2 border-dashed p-8 text-center transition ${
                  draggingSlot === "updater"
                    ? "border-primary bg-primary/10"
                    : "border-slate-700 bg-surface hover:border-slate-600"
                }`}
              >
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleFileSelect("updater")}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  disabled={createRelease.isPending}
                />
                <div className="flex flex-col items-center gap-2">
                  <svg
                    className="h-12 w-12 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <p className="text-sm text-slate-300">
                    Arrastra el updater aquí o{" "}
                    <span className="text-primary">
                      haz clic para seleccionar
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    Archivos soportados: .zip
                  </p>
                </div>
              </div>

              {updater && (
                <div className="mt-4">
                  <div className="flex items-center justify-between rounded-md border border-slate-700 bg-surface p-3">
                    <div className="flex items-center gap-3">
                      <svg
                        className="h-5 w-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-slate-200">
                          {updater.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatFileSize(updater.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUpdater(null)}
                      className="text-red-400 transition hover:text-red-300"
                      disabled={createRelease.isPending}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Notas de la versión
            </label>
            <textarea
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="Describe los cambios en esta versión..."
              rows={4}
              className="w-full rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-primary"
            />
          </div>

          {createRelease.isPending && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-300">Subiendo archivo...</span>
                <span className="text-primary">
                  {createRelease.uploadProgress}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-700">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-primary to-primaryAccent "
                  style={{ width: `${createRelease.uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {error ? (
            <div className="rounded-md bg-red-900/30 px-4 py-2 text-sm text-red-400">
              {error}
            </div>
          ) : null}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={createRelease.isPending}
              className="rounded-md border border-slate-700 bg-background px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-slate-600 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
            >
              {createRelease.isPending ? "Creando..." : "Crear Versión"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGuestReleaseModal;
